import {
  afterNextRender,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationStart,
  NavigationEnd,
  ParamMap,
  Router,
} from '@angular/router';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import {
  DEFAULT_SUGGESTION_SORT,
  IMessagesFilters,
  ISuggestionsFilters,
  serializeMessagesFilters,
  serializeSuggestionsFilters,
  toSuggestionsFilters,
} from 'src/app/filters';
import { AppComponent } from 'src/app/app.component';
import {
  CommunicationUserType,
  CommunicationType,
  MessageStatus,
  ModalQueryParam,
} from 'src/app/models/enums';
import { IMessage } from 'src/app/models';
import { MessageReceiver } from 'src/app/models/enums/messageReceiver';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import { ISuggestion } from 'src/app/models/suggestion';
import { CommunicationsResolver } from 'src/app/resolvers/communications.resolver';
import {
  AuthService,
  BookingsService,
  MessageService,
  ModalUrlService,
  NotificationService,
  StorageService,
  TripsService,
} from 'src/app/services';
import { ModalMessageDetailComponent } from 'src/app/shared/modals/modal-message-detail/modal-message-detail.component';
import { FavouritesService } from 'src/app/services/favourites/favourites.service';
import { ApiRouterService } from 'src/app/services/http/api.router';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialMessagesState } from 'src/app/store/initialisation/messages';
import { initialSuggestionState } from 'src/app/store/initialisation/suggestions';
import { images } from 'src/images';
import { buildNewSuggestion } from 'src/app/utils/suggestionTickets';

@Component({
  selector: 'app-communications',
  templateUrl: './communications.component.html',
  styleUrls: ['./communications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommunicationsComponent implements OnInit, OnDestroy {
  public communicationType: CommunicationType;
  public communicationUserType: CommunicationUserType;

  public suggestionSelected: any;
  public newSuggestion: any;
  public editSuggestion: boolean = false;
  public shouldOpenConversation: boolean = false;
  public suggestions = initialSuggestionState;

  public messageSelected: any;
  public messageDetailSelected: any;
  public newMessage: any;
  public messages = initialMessagesState;

  public messageDetailsList: any = {};
  public messageDetailsPage = 1;

  public images = images;
  public CommunicationTypes = CommunicationType;
  public CommunicationUserType = CommunicationUserType;
  public SuggestionTypes = SuggestionType;
  public SuggestionStatus = SuggestionStatus;
  public SuggestionPriorities = SuggestionPriority;
  public TooltipPosition = TooltipPosition;

  private routerSubscription: Subscription;
  private queryParamsSub?: Subscription;
  private routeParamsSub?: Subscription;
  private interval: any;
  private destroyed = false;

  public searching = false;
  public searchValue: string = '';

  private paramsHandledNavigation: boolean = false;

  public messageDetailModal: Modal;
  public messageDetailShowed: boolean = false;
  private readonly messageDetailModalComponent = viewChild(ModalMessageDetailComponent);

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    private suggestionService: SuggestionService,
    private communicationsResolver: CommunicationsResolver,
    private activatedRoute: ActivatedRoute,
    public utilsService: UtilsService,
    public routerService: ApiRouterService,
    public storageService: StorageService,
    public authService: AuthService,
    public router: Router,
    public notificationService: NotificationService,
    public tripsService: TripsService,
    public favouritesService: FavouritesService,
    public bookingsService: BookingsService,
    public messageService: MessageService,
    public appComponent: AppComponent,
    private modalUrlService: ModalUrlService,
    private injector: Injector,
  ) {
    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById(
        `${this.communicationType}DetailContainer`,
      )!;
      const componentOpen = document.getElementById(
        `${this.communicationType}Container`,
      )!;
      const modals: Node[] = [];
      modals.push(document.getElementById('messageDetail') as Node);
      modals.push(document.getElementById('exceedReceiversModal') as Node);
      modals.push(document.getElementById('additionalCostModal') as Node);
      modals.push(document.getElementById('conversationModal') as Node);
      modals.push(document.getElementById('publicVisibilityModal') as Node);
      const close = utilsService.closeCustomModalsWithModals(
        e,
        modal,
        modals,
        componentOpen,
      );
      if (close && (this.suggestionSelected || this.messageSelected)) {
        this.router.navigate(
          [`${this.communicationType}/${this.communicationUserType}`],
          {
            queryParams: this.activatedRoute.snapshot.queryParams,
          },
        );
      }
    });
  }

  async ngOnInit() {
    this.literalService.setTitle(
      'sidebar.communications',
      ' - NEMI Backoffice',
    );

    this.communicationType = this.activatedRoute.routeConfig?.path?.split(
      '/',
    )[0] as CommunicationType;
    this.communicationUserType = this.activatedRoute.routeConfig?.path?.split(
      '/',
    )[1] as CommunicationUserType;

    this.getCommunicationsListener();

    // Path params drive: opening a suggestion/message detail by :id.
    this.routeParamsSub = this.activatedRoute.paramMap.subscribe((params) => {
      const commId = params.get('id');
      const openConversation = this.isConversationRequested(this.activatedRoute.snapshot.queryParamMap);
      if (commId === 'new') {
        this.suggestionSelected = undefined;
        this.messageSelected = undefined;
        this.openNewCommunication();
      } else if (commId) {
        this.paramsHandledNavigation = true;
        if (this.communicationType === CommunicationType.SUGGESTIONS) {
          this.showSuggestionData({ id: Number(commId), openConversation });
        } else {
          this.showMessageData(Number(commId));
        }
      } else {
        this.suggestionSelected = undefined;
        this.messageSelected = undefined;
        this.newSuggestion = undefined;
        this.newMessage = undefined;
      }
    });

    // Hydration from storage happens in communicationsHydrationGuard before the
    // resolver fires, so there's exactly one API call per sidebar entry.

    // Query params drive: filters, page, search, sort, plus the "open from notification" flow.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(
      (params) => {
        this.applyQueryParamsToState(params);
        this.handleNotificationReentry(params);
      },
    );

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeModals();
      }
      if (event instanceof NavigationEnd) {
        this.communicationType = this.activatedRoute.routeConfig?.path?.split(
          '/',
        )[0] as CommunicationType;
        this.communicationUserType =
          this.activatedRoute.routeConfig?.path?.split(
            '/',
          )[1] as CommunicationUserType;
      }
    });

    this.restoreMessageDetailModal();
  }

  private applyQueryParamsToState(params: ParamMap): void {
    const pageParam = params.get('page');
    const targetState =
      this.communicationType === CommunicationType.SUGGESTIONS
        ? this.suggestions
        : this.messages;
    if (pageParam) {
      const page = Number(pageParam);
      if (!isNaN(page) && page > 0) targetState.pagination.actualPage = page;
    } else {
      targetState.pagination.actualPage = 1;
    }

    this.searchValue = params.get('search') ?? '';
  }

  private handleNotificationReentry(params: ParamMap): void {
    // Skip if the path-params subscription already handled this navigation in the same tick.
    if (this.paramsHandledNavigation) {
      this.paramsHandledNavigation = false;
      return;
    }
    if (!params.get('t')) return;
    const commId = Number(this.activatedRoute.snapshot.params['id']);
    if (!commId) return;
    const openConversation = this.isConversationRequested(params);
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      if (this.suggestionSelected?.id !== commId || openConversation) {
        this.showSuggestionData({ id: commId, openConversation });
      }
    } else if (this.messageSelected?.id !== commId) {
      this.showMessageData(commId);
    }
  }

  private isConversationRequested(params: ParamMap): boolean {
    return params.get('openConversation') === 'true' || params.has(ModalQueryParam.CONVERSATION);
  }

  private async restoreMessageDetailModal() {
    const messageId = Number(await this.modalUrlService.consumeOpenValue(ModalQueryParam.MESSAGE_DETAIL));
    if (this.communicationType !== CommunicationType.MESSAGES || !messageId) return;
    afterNextRender(() => this.showMessageDetail(messageId), { injector: this.injector });
  }

  closeModals() {
    if (this.messageDetailShowed) this.messageDetailModal.toggle();
  }

  getCommunicationsListener() {
    this.interval = setInterval(() => {
      this.refreshData();
    }, 60000);
  }

  // In-place refresh from the resolver (used by the 60s interval). Resolver reads URL.
  async refreshData() {
    this.activatedRoute.snapshot.data['suggestions'] = this.suggestions;
    this.activatedRoute.snapshot.data['messages'] = this.messages;
    this.activatedRoute.snapshot.data['type'] = this.communicationType;
    this.activatedRoute.snapshot.data['userType'] = this.communicationUserType;
    this.searching = true;
    await this.communicationsResolver
      .getCommunications(this.activatedRoute.snapshot, undefined, true)
      .finally(() => {
        this.searching = false;
      });
  }

  async showSuggestionData(data: any) {
    try {
      // Reset first to ensure change detection works when opening conversation again
      this.shouldOpenConversation = false;

      this.suggestionSelected = (await this.suggestionService.getSuggestionById(
        data.id,
      )) as ISuggestion;
      this.newSuggestion = undefined;
      this.editSuggestion = data.edit ?? false;

      if (data.openConversation) {
        await this.modalUrlService.consumeOpenState(ModalQueryParam.CONVERSATION);
        setTimeout(() => {
          this.shouldOpenConversation = true;
        }, 0);
      }

      // Navigate only if the URL doesn't already point at this detail; the paramMap
      // subscription will reenter once the URL matches. Preserves query params.
      const currentId = this.activatedRoute.snapshot.paramMap.get('id');
      if (currentId !== String(data.id)) {
        this.router.navigate(
          [
            `${this.communicationType}/${this.communicationUserType}/${data.id}`,
          ],
          {
            queryParams: this.activatedRoute.snapshot.queryParams,
          },
        );
      }
    } catch {
      this.router.navigate([
        `${this.communicationType}/${this.communicationUserType}`,
      ]);
    }
  }

  changePage(event: any) {
    const { isNext, specificPage } = event;
    const targetState =
      this.communicationType === CommunicationType.SUGGESTIONS
        ? this.suggestions
        : this.messages;
    const current = targetState.pagination.actualPage || 1;
    const newPage = specificPage ?? (isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage > 1 ? newPage : null },
      queryParamsHandling: 'merge',
    });
  }

  closeDetails() {
    if (this.communicationType === this.CommunicationTypes.SUGGESTIONS) {
      this.newSuggestion = undefined;
      this.editSuggestion = false;
      this.shouldOpenConversation = false;
    } else {
      this.newMessage = undefined;
    }
    this.router.navigate(
      [`${this.communicationType}/${this.communicationUserType}`],
      {
        queryParams: this.activatedRoute.snapshot.queryParams,
      },
    );
  }

  addCommunication() {
    this.router.navigate(
      [`${this.communicationType}/${this.communicationUserType}/new`],
      {
        queryParams: this.activatedRoute.snapshot.queryParams,
      },
    );
  }

  private openNewCommunication() {
    if (this.communicationType === this.CommunicationTypes.SUGGESTIONS) {
      this.newSuggestion = buildNewSuggestion(this.communicationUserType);
    } else {
      this.newMessage = {
        client: null,
        receiver:
          this.communicationUserType === CommunicationUserType.USERS
            ? MessageReceiver.USER
            : MessageReceiver.DRIVER,
        receiverIds: [],
        subject: '',
        content: '',
        channels: [],
        receiverServiceId: undefined,
      };
    }
  }

  suggestionEdited(suggestionEdited: ISuggestion) {
    const index = this.suggestions.value.findIndex(
      (suggestion) => suggestion.id === suggestionEdited.id,
    );
    if (index !== -1) {
      this.suggestions.value[index] = suggestionEdited;
    } else {
      this.refreshData();
      this.newSuggestion = undefined;
    }
    if (
      this.suggestionSelected &&
      this.suggestionSelected.id === suggestionEdited.id
    )
      this.suggestionSelected = suggestionEdited;
  }

  // initFilters removed: filter state is now driven by the queryParamMap subscription
  // (applyQueryParamsToState). Storage fallback happens in the resolver / hydration.

  filter(event: any) {
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      const filters = toSuggestionsFilters(event);
      this.storageService.setSuggestionsFilters(JSON.stringify(filters));
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...serializeSuggestionsFilters(filters), page: null },
        queryParamsHandling: 'merge',
      });
    } else {
      const filters: IMessagesFilters = {
        selectedClients: event.selectedClients ?? [],
        selectedSenders: event.selectedSenders ?? [],
        selectedReceivers: event.selectedReceivers ?? [],
        selectedOrigin: event.selectedOrigin ?? null,
        selectedEvents: event.selectedEvents ?? [],
        selectedAdminAlerts: event.selectedAdminAlerts ?? null,
      };
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...serializeMessagesFilters(filters), page: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  search(data: any) {
    const value = typeof data === 'object' ? data.target.value : data;
    const currentSearch =
      this.activatedRoute.snapshot.queryParamMap.get('search') || null;
    if ((value || null) === currentSearch) {
      this.refreshData();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: value || null, page: null },
      queryParamsHandling: 'merge',
    });
  }

  sortSuggestions() {
    this.storageService.getSuggestionsSortType().then((sort) => {
      const isDefault = !sort || sort === DEFAULT_SUGGESTION_SORT;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { sort: isDefault ? null : sort },
        queryParamsHandling: 'merge',
      });
    });
  }

  changeCommunication(value: CommunicationType) {
    // Switching SUGGESTIONS <-> MESSAGES uses a different filter set; do not preserve queryParams.
    this.communicationType = value;
    this.router.navigate([
      `${this.communicationType}/${this.communicationUserType}`,
    ]);
  }

  changeCommunicationUserType() {
    const currentUserType = this.activatedRoute.routeConfig?.path?.split('/')[1] as CommunicationUserType;
    this.communicationUserType =
      currentUserType === CommunicationUserType.DRIVERS ? CommunicationUserType.USERS : CommunicationUserType.DRIVERS;
    // Filters are scoped to a user type; do not preserve queryParams across the switch.
    this.router.navigate([
      `${this.communicationType}/${this.communicationUserType}`,
    ]);
  }

  updatePriority(data: any) {
    const suggestionToUpdate = structuredClone(
      this.suggestions.value.find((suggestion) => suggestion.id === data.id),
    );
    if (suggestionToUpdate !== undefined) {
      suggestionToUpdate.priority = data.value;
      this.suggestionService
        .updateSuggestion(suggestionToUpdate)
        .then((response) => {
          this.suggestionEdited(response as ISuggestion);
        });
    }
  }

  updateStatus(data: any) {
    const suggestionToUpdate = structuredClone(
      this.suggestions.value.find((suggestion) => suggestion.id === data.id),
    );
    if (suggestionToUpdate !== undefined) {
      suggestionToUpdate.status = data.value;
      this.suggestionService
        .updateSuggestion(suggestionToUpdate)
        .then((response) => {
          this.suggestionEdited(response as ISuggestion);
        });
    }
  }

  getCommentsText() {
    if (this.communicationUserType === CommunicationUserType.USERS)
      return CommunicationUserType.DRIVERS;
    return CommunicationUserType.USERS;
  }

  async showMessageData(id: number) {
    this.messageSelected = await this.findOrLoadMessage(id);
    this.newMessage = undefined;
    const currentId = this.activatedRoute.snapshot.paramMap.get('id');
    if (currentId !== String(id)) {
      this.router.navigate(
        [`${this.communicationType}/${this.communicationUserType}/${id}`],
        {
          queryParams: this.activatedRoute.snapshot.queryParams,
        },
      );
    }
  }

  async showMessageDetail(id: number) {
    const modalComponent = this.messageDetailModalComponent();
    const message = await this.findOrLoadMessage(id);
    if (!modalComponent || !message) return;
    this.messageDetailModal = new Modal(modalComponent.modalContainer().nativeElement, {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.messageDetailShowed = false;
        this.modalUrlService.markClosed(ModalQueryParam.MESSAGE_DETAIL);
      },
      onShow: () => (this.messageDetailShowed = true),
    });
    this.messageDetailSelected = { ...message };
    this.messageDetailsList = await this.getMessageDetail(
      id,
      this.messageDetailsPage,
      this.messageService.getInitialDeliveryStatus(message),
    );
    this.messageSelected = undefined;
    this.newMessage = undefined;
    this.messageDetailModal.toggle();
    this.modalUrlService.markOpen(ModalQueryParam.MESSAGE_DETAIL, String(id));
  }

  private async findOrLoadMessage(id: number): Promise<IMessage | undefined> {
    const listedMessage = this.messages.value.find((message) => message.id === id);
    if (listedMessage) return listedMessage;
    return this.messageService.getMessageById(id).catch(() => undefined);
  }

  openUserDetail(id: number) {
    window.open(
      `/${this.communicationUserType}/${id}`,
      '_blank',
    );
  }

  async updateMessageDetail(event: any) {
    if (event.pagination) {
      if (event.pagination.specificPage)
        this.messageDetailsPage = event.pagination.specificPage;
      else if (event.pagination.isNext) this.messageDetailsPage += 1;
      else this.messageDetailsPage -= 1;
    }
    this.messageDetailsList = await this.getMessageDetail(
      this.messageDetailSelected.id,
      this.messageDetailsPage,
      event.status,
      event.searchValue,
    );
  }

  async getMessageDetail(
    id: number,
    page: number,
    status: MessageStatus,
    searchValue?: string,
  ) {
    return await this.messageService.getMessageDetails(
      id,
      page,
      10,
      status,
      searchValue,
    );
  }

  abortFetchEventSource() {
    clearInterval(this.interval);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.interval);
    this.routerSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.routeParamsSub?.unsubscribe();
  }
}
